// Producción del día: efectivos × menú → OP consolidada → consumo kardex → rendimiento.
// Catering → Operación → Producción del día.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardList, Factory, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, inv, num } from '@/features/inventario/shared';
import {
  OP_ESTADO_CLS, OP_ESTADO_LABEL, OpDetalle, OpResumen, SERVICIO_LABEL,
  catRpc, invalidateCatering, money,
} from './shared';

interface BodegaOpt { id: string; codigo: string; nombre: string }

const hoyLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function ProduccionCatering() {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState(hoyLocal());
  const [bodega, setBodega] = useState('');
  const [selId, setSelId] = useState('');
  const [reales, setReales] = useState<Record<string, string>>({});
  const invalidar = () => invalidateCatering(qc);

  const { data: bodegas = [] } = useQuery<BodegaOpt[]>({
    queryKey: ['cat-bodegas'],
    queryFn: () => inv.list<BodegaOpt>('locations', '?select=id,codigo,nombre&deleted_at=is.null&es_activo=eq.true&order=nombre'),
  });
  const bodegaSel = bodega || bodegas.find((b) => b.codigo === 'CC01')?.id || bodegas[0]?.id || '';

  const { data: ordenes = [] } = useQuery<OpResumen[]>({
    queryKey: ['cat-ops'],
    queryFn: () => catRpc<OpResumen[]>('op_listar', { _estado: null }),
  });
  const { data: det } = useQuery<OpDetalle>({
    queryKey: ['cat-op', selId], enabled: !!selId,
    queryFn: () => catRpc<OpDetalle>('op_obtener', { _orden_id: selId }),
  });

  const generar = useMutation({
    mutationFn: () => catRpc<OpDetalle>('op_generar', { _fecha: fecha, _bodega_mp_id: bodegaSel }),
    onSuccess: (r) => { toast.success(`Orden ${r.codigo} generada: ${num(r.porciones_plan, 0)} porciones`); setSelId(r.id); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const confirmar = useMutation({
    mutationFn: (id: string) => catRpc<{ costo_mp: number }>('op_confirmar', { _orden_id: id }),
    onSuccess: (r) => { toast.success(`Materia prima consumida del kardex: ${money(r.costo_mp, 2)}`); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: (id: string) => catRpc<{ costo_porcion_real: number }>('op_cerrar', {
      _orden_id: id,
      _reales: (det?.produccion ?? [])
        .filter((p) => reales[p.receta_id])
        .map((p) => ({ receta_id: p.receta_id, porciones_reales: parseFloat(reales[p.receta_id]) })),
    }),
    onSuccess: (r) => { toast.success(`Cerrada — costo real por porción ${money(r.costo_porcion_real)}`); setReales({}); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => catRpc('op_anular', { _orden_id: id }),
    onSuccess: () => { toast.success('Orden anulada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const stockInsuficiente = useMemo(() =>
    (det?.consumos ?? []).filter((c) => Number(c.stock) < Number(c.cantidad_plan)).length, [det]);

  return (
    <div className="space-y-4 p-4">
      {/* Generar */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><Factory className="h-4 w-4" /> Generar producción del día</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div>
            <p className="pb-1 text-xs text-muted-foreground">Fecha de servicio</p>
            <input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div>
            <p className="pb-1 text-xs text-muted-foreground">Bodega de materia prima</p>
            <select className={`${fieldCls} w-56`} value={bodegaSel} onChange={(e) => setBodega(e.target.value)}>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </div>
          <Button disabled={generar.isPending || !bodegaSel} onClick={() => generar.mutate()}>
            {generar.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardList className="mr-2 h-4 w-4" />}
            Generar OP (efectivos × menú del día)
          </Button>
          <p className="text-xs text-muted-foreground">
            Toma los pax registrados por contrato, arma el menú del día del ciclo y explota
            recetas y subrecetas hasta materia prima.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Listado */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-base">Órdenes de producción</CardTitle></CardHeader>
          <CardContent className="max-h-[62vh] space-y-1 overflow-y-auto">
            {ordenes.map((o) => (
              <button key={o.id} onClick={() => setSelId(o.id)}
                className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-accent ${selId === o.id ? 'border-primary bg-accent' : 'border-transparent'}`}>
                <span>
                  <span className="font-medium">{o.codigo}</span>
                  <span className="block text-xs text-muted-foreground">{o.fecha} · {o.bodega_mp}</span>
                </span>
                <span className="text-right">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${OP_ESTADO_CLS[o.estado]}`}>{OP_ESTADO_LABEL[o.estado]}</span>
                  <span className="block pt-0.5 font-mono text-xs">{num(o.porciones_plan, 0)} porc · {money(o.costo_mp, 2)}</span>
                </span>
              </button>
            ))}
            {!ordenes.length && <p className="text-sm text-muted-foreground">Sin órdenes todavía — genere la primera.</p>}
          </CardContent>
        </Card>

        {/* Detalle */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span>{det ? `${det.codigo} — ${det.fecha}` : 'Detalle de la orden'}</span>
              {det && (
                <span className="flex gap-2">
                  {det.estado === 'borrador' && (
                    <>
                      <Button size="sm" disabled={confirmar.isPending} onClick={() => confirmar.mutate(det.id)}>
                        <CheckCircle2 className="mr-1 h-4 w-4" /> Confirmar (consumir MP)
                      </Button>
                      <Button size="sm" variant="outline" disabled={anular.isPending} onClick={() => anular.mutate(det.id)}>
                        <XCircle className="mr-1 h-4 w-4" /> Anular
                      </Button>
                    </>
                  )}
                  {det.estado === 'en_proceso' && (
                    <Button size="sm" disabled={cerrar.isPending} onClick={() => cerrar.mutate(det.id)}>
                      <CheckCircle2 className="mr-1 h-4 w-4" /> Cerrar (rendimiento)
                    </Button>
                  )}
                </span>
              )}
            </CardTitle>
            {det && (
              <p className="text-sm text-muted-foreground">
                <span className={`mr-2 rounded-full px-2 py-0.5 text-xs ${OP_ESTADO_CLS[det.estado]}`}>{OP_ESTADO_LABEL[det.estado]}</span>
                {num(det.porciones_plan, 0)} porciones plan
                {det.porciones_reales > 0 && ` · ${num(det.porciones_reales, 0)} reales`}
                {det.costo_mp > 0 && <> · MP consumida <b>{money(det.costo_mp, 2)}</b>
                  {det.porciones_reales > 0 && <> · costo real/porción <b className="text-primary">{money(det.costo_mp / det.porciones_reales)}</b></>}</>}
              </p>
            )}
          </CardHeader>
          {det && (
            <CardContent className="max-h-[56vh] space-y-4 overflow-y-auto">
              {/* Platos a servir */}
              <div>
                <p className="pb-1 text-sm font-medium">Qué se sirve (menú × pax)</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2">Servicio</th><th className="py-1 pr-2">Plato</th>
                    <th className="py-1 pr-2 text-right">Porciones</th><th className="py-1 pr-2 text-right">Costo/porción</th>
                  </tr></thead>
                  <tbody>
                    {det.platos.map((p, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1 pr-2 text-xs text-muted-foreground">{SERVICIO_LABEL[p.servicio] ?? p.servicio}</td>
                        <td className="py-1 pr-2">{p.nombre}</td>
                        <td className="py-1 pr-2 text-right font-mono">{num(p.porciones_plan, 0)}{p.porciones_reales != null && ` → ${num(p.porciones_reales, 0)}`}</td>
                        <td className="py-1 pr-2 text-right font-mono">{money(p.costo_porcion)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Producción consolidada */}
              <div>
                <p className="pb-1 text-sm font-medium">Qué produce la cocina (batch consolidado por receta)</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2">Receta</th><th className="py-1 pr-2 text-right">Porciones plan</th>
                    <th className="py-1 pr-2 text-right">Porciones reales</th>
                  </tr></thead>
                  <tbody>
                    {det.produccion.map((p) => (
                      <tr key={p.receta_id} className="border-b last:border-0">
                        <td className="py-1 pr-2">{p.nombre}</td>
                        <td className="py-1 pr-2 text-right font-mono">{num(p.porciones_plan, 0)}</td>
                        <td className="py-1 pr-2 text-right">
                          {det.estado === 'en_proceso' ? (
                            <input className={`${fieldCls} w-24 text-right`} placeholder={String(Math.round(p.porciones_plan))}
                              value={reales[p.receta_id] ?? ''} onChange={(e) => setReales((s) => ({ ...s, [p.receta_id]: e.target.value }))} />
                          ) : <span className="font-mono">{p.porciones_reales != null ? num(p.porciones_reales, 0) : '—'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Consumo de MP */}
              <div>
                <p className="pb-1 text-sm font-medium">
                  Qué entrega bodega (explosión a materia prima)
                  {stockInsuficiente > 0 && det.estado === 'borrador' && (
                    <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900/40 dark:text-red-300">
                      {stockInsuficiente} ítems sin stock suficiente
                    </span>
                  )}
                </p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Materia prima</th>
                    <th className="py-1 pr-2 text-right">Necesario</th><th className="py-1 pr-2 text-right">Stock</th>
                    <th className="py-1 pr-2 text-right">Consumido</th><th className="py-1 pr-2 text-right">Costo unit.</th>
                  </tr></thead>
                  <tbody>
                    {det.consumos.map((c, i) => (
                      <tr key={c.material_id} className={`border-b last:border-0 ${Number(c.stock) < Number(c.cantidad_plan) && det.estado === 'borrador' ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                        <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                        <td className="py-1 pr-2">{c.nombre}<span className="ml-1 text-xs text-muted-foreground">{c.unidad}</span></td>
                        <td className="py-1 pr-2 text-right font-mono">{num(c.cantidad_plan)}</td>
                        <td className="py-1 pr-2 text-right font-mono">{num(c.stock)}</td>
                        <td className="py-1 pr-2 text-right font-mono">{c.cantidad_consumida > 0 ? num(c.cantidad_consumida) : '—'}</td>
                        <td className="py-1 pr-2 text-right font-mono">{c.costo_unit > 0 ? money(c.costo_unit) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
